import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Trophy, Crown, Share2 } from "lucide-react";
import { API } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import ThemeToggle from "../components/ThemeToggle";
import AuthButton from "../components/AuthButton";
import { Toaster } from "../components/ui/sonner";

const LIMITS = [10, 25, 50];

export default function Leaderboard() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [limit, setLimit] = useState(25);
    const [mine, setMine] = useState(null);
    const [handle, setHandle] = useState("");
    const [busy, setBusy] = useState(false);

    const fetchBoard = async (lim) => {
        try {
            const { data } = await axios.get(`${API}/leaderboard?limit=${lim}`);
            setItems(data.items || []);
        } catch { setItems([]); }
    };

    useEffect(() => { fetchBoard(limit); }, [limit]);

    useEffect(() => {
        if (!user) { setMine(null); return; }
        axios.get(`${API}/leaderboard/me`, { withCredentials: true })
            .then(({ data }) => {
                setMine(data);
                setHandle(data.handle || data.suggested_handle || "");
            })
            .catch(() => setMine(null));
    }, [user]);

    const handleOptIn = async (next) => {
        if (!handle && next) {
            toast.error("Pick a handle (3–15 chars)");
            return;
        }
        setBusy(true);
        try {
            const { data } = await axios.post(
                `${API}/leaderboard/me`,
                { opt_in: next, handle: next ? handle : null },
                { withCredentials: true },
            );
            setMine({ ...mine, opt_in: data.opt_in, handle: data.handle });
            toast.success(next ? "You're on the board." : "Removed from leaderboard.");
            fetchBoard(limit);
        } catch (e) {
            const msg = e?.response?.data?.detail || "Failed";
            toast.error(msg);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="relative min-h-screen w-full bg-app overflow-x-hidden" data-testid="leaderboard-screen">
            <div className="absolute inset-0 aurora-wash opacity-30 pointer-events-none" />
            <div className="absolute inset-0 grain pointer-events-none" />
            <Toaster theme="dark" position="bottom-center" />

            <header className="relative z-10 max-w-md mx-auto px-6 pt-6 flex items-center justify-between">
                <button onClick={() => navigate("/")} className="flex items-center gap-2 text-app-2 hover:text-app transition-colors" data-testid="leaderboard-back">
                    <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
                    <span className="font-sans text-xs uppercase tracking-[0.3em]">home</span>
                </button>
                <div className="flex items-center gap-2">
                    <ThemeToggle />
                    <AuthButton compact />
                </div>
            </header>

            <div className="relative z-10 max-w-md mx-auto px-6 pt-8 pb-24 space-y-7">
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                    <p className="font-sans text-[10px] uppercase tracking-[0.4em] text-app-3 mb-3 flex items-center gap-2">
                        <Trophy className="w-3 h-3" strokeWidth={1.5} /> referrers · public · opt-in
                    </p>
                    <h2 className="font-serif font-light text-app text-3xl sm:text-4xl leading-tight tracking-tight">
                        the <span className="italic text-app-2">quiet hall</span> of sharers.
                    </h2>
                    <p className="mt-3 font-sans text-sm text-app-3 max-w-[34ch]">
                        Handles only. No avatars, no real names. Sharing MoodCard with friends earns you a spot.
                    </p>
                </motion.div>

                {/* Opt-in card */}
                {user && mine && (
                    <section className="rounded-2xl border border-app/20 bg-surface-1 p-5" data-testid="leaderboard-opt-in">
                        <div className="flex items-center justify-between mb-3">
                            <span className="font-sans text-[10px] uppercase tracking-[0.35em] text-app-3">
                                your handle
                            </span>
                            <button
                                onClick={() => handleOptIn(!mine.opt_in)}
                                disabled={busy}
                                data-testid="leaderboard-opt-toggle"
                                aria-pressed={mine.opt_in}
                                className="relative h-5 w-9 rounded-full transition-colors"
                                style={{ backgroundColor: mine.opt_in ? "var(--app-text)" : "var(--surface-3, rgba(255,255,255,0.09))" }}
                            >
                                <span
                                    className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full transition-transform ${mine.opt_in ? "translate-x-4" : ""}`}
                                    style={{ backgroundColor: mine.opt_in ? "var(--app-bg)" : "var(--app-text)" }}
                                />
                            </button>
                        </div>
                        <input
                            value={handle}
                            onChange={(e) => setHandle(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "").slice(0, 15))}
                            placeholder="3–15 chars · a-z 0-9 -"
                            data-testid="leaderboard-handle-input"
                            className="w-full bg-surface-2 border border-app/30 rounded-full px-4 py-2 text-app font-sans text-sm outline-none focus:border-app/60 placeholder:text-app-3"
                        />
                        <p className="mt-2 font-sans text-[11px] text-app-3">
                            {mine.opt_in
                                ? "You're visible. Toggle off to hide."
                                : `Suggested: ${mine.suggested_handle}. Toggle on to appear.`}
                        </p>
                    </section>
                )}

                {/* Limit picker */}
                <div className="flex items-center justify-between">
                    <span className="font-sans text-[10px] uppercase tracking-[0.35em] text-app-3">top</span>
                    <div className="flex gap-1.5">
                        {LIMITS.map((n) => (
                            <button
                                key={n}
                                onClick={() => setLimit(n)}
                                data-testid={`leaderboard-limit-${n}`}
                                className={`px-3 py-1.5 rounded-full border text-xs font-sans transition-colors ${
                                    n === limit ? "border-app bg-cta text-app-inverse" : "border-app/30 text-app-2 hover:text-app"
                                }`}
                            >
                                {n}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Board */}
                <section data-testid="leaderboard-list">
                    {items.length === 0 ? (
                        <div className="rounded-2xl border border-app/20 bg-surface-1 p-8 text-center">
                            <p className="font-serif italic text-app-2 text-lg">no one has opted in yet.</p>
                            <p className="mt-2 font-sans text-sm text-app-3">Be the first.</p>
                        </div>
                    ) : (
                        <ol className="space-y-2">
                            {items.map((it, i) => (
                                <motion.li
                                    key={it.handle}
                                    initial={{ opacity: 0, y: 6 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.35, delay: i * 0.03 }}
                                    className="flex items-center gap-3 p-3 rounded-xl border border-app/20 bg-surface-1"
                                >
                                    <span className={`w-7 text-right font-serif italic text-app-3 ${i < 3 ? "text-app" : ""}`}>
                                        {i + 1}
                                    </span>
                                    <div className="flex-1 min-w-0 flex items-center gap-2">
                                        <span className="font-mono text-app text-sm truncate">{it.handle}</span>
                                        {it.premium_via_referral && (
                                            <Crown className="w-3 h-3 text-app-2" strokeWidth={1.5} />
                                        )}
                                    </div>
                                    <span className="font-sans text-xs text-app-3">{it.count} shared</span>
                                </motion.li>
                            ))}
                        </ol>
                    )}
                </section>

                {!user && (
                    <p className="text-center font-sans text-xs text-app-3">
                        Sign in to claim a handle and join the board.
                    </p>
                )}
            </div>
        </div>
    );
}
